import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../api';
import { CompactTable } from '@table-library/react-table-library/compact';
import { useTheme } from '@table-library/react-table-library/theme';
import { getTheme } from '@table-library/react-table-library/baseline';
import { useSort } from '@table-library/react-table-library/sort';
import BaseLayout from './BaseLayout';

const adjustDateForEasternTime = (gameDate, gameTime) => {
  const [day, month, year] = gameDate.split('/').map(Number);
  const eventYear = year || 2024; // Default to 2024 if the year is missing
  let [hour, minute] = gameTime.match(/(\d+):(\d+)(am|pm)/).slice(1, 3);
  hour = parseInt(hour);
  minute = parseInt(minute);
  const isPM = gameTime.includes('pm');
  if (isPM && hour < 12) hour += 12;
  if (!isPM && hour === 12) hour = 0;
  const londonDate = new Date(Date.UTC(eventYear, month - 1, day, hour, minute));
  const easternDate = new Date(londonDate.getTime() - 5 * 60 * 60 * 1000);
  const adjustedDate = easternDate.getUTCHours() < 5
    ? new Date(easternDate.getTime() - 24 * 60 * 60 * 1000)
    : easternDate;
  return `${String(adjustedDate.getUTCMonth() + 1).padStart(2, '0')}/${String(adjustedDate.getUTCDate()).padStart(2, '0')}/${adjustedDate.getUTCFullYear()}`;
};

const ITEMS_PER_PAGE = 20;

const ManagePicks = () => {
  const [nodes, setNodes] = useState([]);
  const theme = useTheme(getTheme());
  const [isLoading, setIsLoading] = useState(true);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    document.body.classList.add("account-page-bg")
    fetchGamesAndPicks();
    return () => {
      document.body.classList.remove("account-page-bg")
    }
  }, []);

  const fetchGamesAndPicks = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const picksResponse = await axios.get(`${API_BASE}/data/get-my-picks-enriched`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const enrichedPicks = picksResponse.data.map((pick) => {
        const gameDateAdjusted = (pick.game_date && pick.game_time) ? adjustDateForEasternTime(pick.game_date, pick.game_time) : 'Unknown Date';
        return {
          id: pick.id,
          ...pick,
          pick_time: new Date(pick.pick_time),
          game: (pick.home_team && pick.away_team) ? `${pick.home_team.toUpperCase()} vs ${pick.away_team.toUpperCase()}` : 'Unknown Game',
          gameDate: gameDateAdjusted,
        };
      });
  
      // Sort by pick_time in descending order (newest first)
      enrichedPicks.sort((a, b) => b.pick_time - a.pick_time);
  
      setNodes(enrichedPicks);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sort = useSort(
    { nodes },
    {
      onChange: onSortChange,
    },
    {
      sortFns: {
        GAME: (array) => array.sort((a, b) => a.game.localeCompare(b.game)),
        PICKED_TEAM: (array) => array.sort((a, b) => a.picked_team.localeCompare(b.picked_team)),
        PICK_TIME: (array) => array.sort((a, b) => new Date(a.pick_time) - new Date(b.pick_time)),
        RESULT: (array) => array.sort((a, b) => (a.result !== null ? 1 : 0) - (b.result !== null ? 1 : 0)),
      },
    }
  );

  function onSortChange(action, state) {
    //console.log(action, state);
  }

  const deletePick = async (id) => {
    setDeletingIds(prev => new Set(prev.add(id)));
    try {
      await axios.delete(`${API_BASE}/data/delete-gamepick/${id}`);
      setNodes(prev => prev.filter(pick => pick.id !== id));
    } catch (error) {
      console.error('Error deleting pick:', error);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const COLUMNS = [
    { label: 'Sport', renderCell: (item) => item.sport },
    { label: 'Game', renderCell: (item) => item.game },
    {
      label: 'Game Date',
      renderCell: (item) => {
        const [month, day, year] = item.gameDate.split('/');
        return `${month}/${day}/${year}`;
      },
    },
    { label: 'Picked Team', renderCell: (item) => item.picked_team.toUpperCase() },
    {
      label: 'Pick Time',
      renderCell: (item) =>
        item.pick_time.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
    },
    {
      label: 'Result',
      renderCell: (item) => {
        if (item.result === null) return <span style={{ color: '#888' }}>Pending</span>;
        return item.result === 1
          ? <span style={{ color: 'green', fontWeight: 'bold' }}>Win</span>
          : <span style={{ color: 'red', fontWeight: 'bold' }}>Loss</span>;
      },
    },
    {
      label: 'Action',
      renderCell: (item) => item.result === null ? (
        <button onClick={() => deletePick(item.id)} disabled={deletingIds.has(item.id)}>
          {deletingIds.has(item.id) ? 'Deleting...' : 'Delete'}
        </button>
      ) : null,
    },
  ];

  const totalPages = Math.ceil(nodes.length / ITEMS_PER_PAGE);
  const paginatedNodes = nodes.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const data = { nodes: paginatedNodes };

  return (
    <BaseLayout>
      <div className='history-container'>
        <h2>Your Picks</h2>
        <CompactTable columns={COLUMNS} data={data} theme={theme} sort={sort} />
        {isLoading ? (
          <p>Loading picks...</p>
        ) : nodes.length === 0 ? (
          <p>You have not made any picks.</p>
        ) : null}
        {!isLoading && nodes.length > ITEMS_PER_PAGE && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px', fontFamily: 'Montserrat, sans-serif', fontSize: '0.95rem' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{ padding: '4px 12px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              ← Prev
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{ padding: '4px 12px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </BaseLayout>
  );
};

export default ManagePicks;
